import {
  MediaPlayer,
  MediaPlayerInstance,
  MediaProvider,
  Poster,
  useMediaStore,
} from '@vidstack/react';
import { defaultLayoutIcons, DefaultVideoLayout } from '@vidstack/react/player/layouts/default';
import {
  FunctionComponent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ConfigContext, UserContext } from '../../../context';
import { useBeforeUnload, useDuration, useFreeLoginPopup } from '../../hooks';
import { Copyright } from '../Copyright';

import '@vidstack/react/player/styles/default/layouts/video.css';
import '@vidstack/react/player/styles/default/theme.css';

import classNames from 'classnames';
import classes from './VideoPlayer.module.scss';

interface VideoPlayerProps {
  url?: string;
  thumb?: string;
  caption?: string;
  captionClassName?: string;
  copyright?: string;
  autoplay?: boolean;
  onLogItemPlayed?: (duration: number) => void;
  onLogClickOpenArticle?: () => void;
  className?: string;
}

export const VideoPlayer: FunctionComponent<VideoPlayerProps> = ({
  url,
  thumb,
  caption,
  captionClassName,
  copyright,
  autoplay,
  onLogItemPlayed,
  onLogClickOpenArticle,
  className,
}) => {
  const playerRef = useRef<MediaPlayerInstance>(null);

  const media = useMediaStore(playerRef);

  const { freeLoginModal, freeLoginHandler } = useFreeLoginPopup();

  const { isAnonymous } = useContext(UserContext).userProfile;

  const { actionsDisabled = true } = { ...useContext(ConfigContext).config.elements.freeLogin };

  const mediaButtonDisabled = useMemo(
    () => isAnonymous && actionsDisabled,
    [actionsDisabled, isAnonymous]
  );

  const { startTrackTime, stopTrackTime, getDuration } = useDuration();

  const [activated, setActivated] = useState(false);

  const logItemPlayed = useCallback(
    () => onLogItemPlayed?.(Math.min(Math.floor(media.duration), getDuration())),
    [getDuration, media.duration, onLogItemPlayed]
  );

  useBeforeUnload(logItemPlayed);

  useEffect(() => {
    return () => logItemPlayed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPlay = useCallback(() => {
    const player = playerRef.current;

    if (!player) {
      return;
    }

    if (mediaButtonDisabled) {
      player.pause();
      freeLoginHandler();
      return;
    }

    setActivated(true);

    startTrackTime();

    onLogClickOpenArticle?.();

    const videoElement = player.el?.querySelector('video');

    if (!videoElement) {
      return;
    }

    document.querySelectorAll('video').forEach((video) => {
      if (video !== videoElement) {
        video.pause();
      }
    });
  }, [freeLoginHandler, mediaButtonDisabled, onLogClickOpenArticle, startTrackTime]);

  const onPause = useCallback(() => {
    logItemPlayed();
    stopTrackTime();
  }, [logItemPlayed, stopTrackTime]);

  useEffect(() => {
    if (autoplay) {
      setActivated(true);
    }
  }, [autoplay]);

  if (!url) {
    return null;
  }

  return (
    <>
      <MediaPlayer
        className={classNames(className)}
        ref={playerRef}
        src={url}
        autoPlay={autoplay}
        onPlay={onPlay}
        onPause={onPause}
        load={'visible'}
      >
        <MediaProvider>
          <Poster src={thumb} className={'vds-poster'} />
        </MediaProvider>

        <DefaultVideoLayout icons={defaultLayoutIcons} />

        {copyright && !activated && <Copyright copyright={copyright} />}
      </MediaPlayer>

      {caption && <div className={classNames(captionClassName, classes['caption'])}>{caption}</div>}

      {freeLoginModal}
    </>
  );
};
